import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Cpu, Network, Database, Sparkles, Save } from 'lucide-react';

const ARCHITECTURES = [
  { id: 'Basic LLM Generation', icon: <Sparkles className="w-4 h-4 text-amber-500" />, desc: 'Simple prompt-in, text-out tasks.' },
  { id: 'RAG (Retrieval-Augmented)', icon: <Database className="w-4 h-4 text-blue-500" />, desc: 'Chat with internal documents/data.' },
  { id: 'Agentic Automation', icon: <Network className="w-4 h-4 text-purple-500" />, desc: 'Multi-step, autonomous tool-calling.' },
  { id: 'Traditional/Predictive ML', icon: <Cpu className="w-4 h-4 text-emerald-500" />, desc: 'Classification, forecasting, or routing.' }
];

export default function WorkflowCapabilityMapper() {
  const { user, token, isImpersonating } = useAuth();
  const navigate = useNavigate();
  
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const fetchData = async () => {
    // 🚨 ARCHITECTURE RULE 1: Race Condition Guard
    if (!user || !user.org_id) return;

    try {
      setLoading(true);
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      
      // Fetching existing workflows
      const res = await axios.get(`${baseURL}/api/sprint/workflow-map/${user.org_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Initialize any empty suitability fields to 'Unassigned'
      const loadedWorkflows = (res.data || []).map(wf => ({
        ...wf,
        ai_suitability: wf.ai_suitability || 'Unassigned'
      }));
      
      setWorkflows(loadedWorkflows);
    } catch (error) {
      console.error("Failed to load workflows", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.org_id, token]);

  const handleSuitabilityChange = (id, newSuitability) => {
    setWorkflows(prev => prev.map(wf => 
      wf.id === id ? { ...wf, ai_suitability: newSuitability } : wf
    ));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      
      // We safely reuse the existing workflow-map POST route (upsert logic)
      await axios.post(`${baseURL}/api/sprint/workflow-map`, {
        orgId: user.org_id,
        workflows: workflows
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setMessage({ type: 'success', text: 'Architectural capabilities successfully mapped and saved.' });
    } catch (error) {
      console.error("Save failed", error);
      setMessage({ type: 'error', text: 'Failed to save capability map. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-8 text-slate-500 text-center font-medium">Loading Workflow Data...</div>;

  return (
    <div className="max-w-7xl mx-auto p-6 pb-24">
     

      <div className="flex justify-between items-end mb-8 border-b border-slate-200 pb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Day 4: Capability Mapper</h1>
          <p className="text-slate-500 mt-2">Map identified business workflows to specific technical AI architectures.</p>
        </div>
        
        <button 
          onClick={handleSave}
          disabled={saving || workflows.length === 0 || isImpersonating}
          className={`flex items-center gap-2 px-6 py-2.5 font-bold rounded-lg shadow-sm transition-all ${
            isImpersonating 
              ? 'bg-slate-300 text-slate-500 cursor-not-allowed' 
              : 'bg-blue-600 text-white hover:bg-blue-700 hover:shadow-md'
          }`}
        >
          <Save className="w-4 h-4" />
          {isImpersonating ? '🔒 Read-Only' : (saving ? 'Saving...' : 'Save Capability Map')}
        </button>
      </div>

      {message.text && (
        <div className={`p-4 rounded-xl mb-6 text-sm font-bold ${message.type === 'success' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
          {message.type === 'success' ? '✅ ' : '❌ '}{message.text}
        </div>
      )}

      {workflows.length === 0 ? (
        <div className="bg-white p-12 rounded-2xl border border-slate-200 shadow-sm text-center">
          <div className="text-4xl mb-4">🗺️</div>
          <h3 className="text-xl font-bold text-slate-800 mb-2">No Workflows Found</h3>
          <p className="text-slate-500 text-sm mb-6">You must complete the Day 2 Workflow Mapping intake before you can assign architectures.</p>
          <button 
            onClick={() => navigate('/workflow-mapping')}
            className="px-6 py-2.5 bg-slate-900 text-white font-bold rounded-lg hover:bg-slate-800"
          >
            Go to Workflow Mapper
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {workflows.map((wf) => (
            <div key={wf.id} className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex flex-col md:flex-row md:items-center gap-6 transition-all hover:border-blue-300">
              
              <div className="flex-1">
                <h3 className="font-bold text-slate-900 text-lg">{wf.process_name}</h3>
                <p className="text-sm text-slate-500 mt-1">
                  <span className="font-semibold text-slate-700">Friction:</span> {wf.identified_bottleneck}
                </p>
                <div className="flex gap-3 mt-3">
                  <span className="text-xs px-2.5 py-1 bg-slate-100 text-slate-600 rounded-md font-medium border border-slate-200">
                    {wf.current_tools_used || 'Manual'}
                  </span>
                  {wf.hours_spent_per_week && (
                    <span className="text-xs px-2.5 py-1 bg-rose-50 text-rose-700 rounded-md font-medium border border-rose-100">
                      ⏱ {wf.hours_spent_per_week} hrs/week
                    </span>
                  )}
                </div>
              </div>

              <div className="md:w-80 shrink-0 bg-slate-50 p-4 rounded-lg border border-slate-100">
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                  Target AI Architecture
                </label>
                <select 
                  value={wf.ai_suitability}
                  onChange={(e) => handleSuitabilityChange(wf.id, e.target.value)}
                  className={`w-full p-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-semibold transition-colors ${
                    wf.ai_suitability === 'Unassigned' ? 'border-amber-300 bg-amber-50 text-amber-700' : 'border-slate-300 bg-white text-slate-800'
                  }`}
                >
                  <option value="Unassigned">⚠️ Select Architecture...</option>
                  {ARCHITECTURES.map(arch => (
                    <option key={arch.id} value={arch.id}>{arch.id}</option>
                  ))}
                  <option value="Not Viable for AI">❌ Not Viable for AI</option>
                </select>
                
                {/* Dynamic Architecture Description Helper */}
                {wf.ai_suitability !== 'Unassigned' && wf.ai_suitability !== 'Not Viable for AI' && (
                  <div className="mt-2 flex items-start gap-1.5 text-xs text-slate-500">
                    {ARCHITECTURES.find(a => a.id === wf.ai_suitability)?.icon}
                    <span>{ARCHITECTURES.find(a => a.id === wf.ai_suitability)?.desc}</span>
                  </div>
                )}
              </div>

            </div>
          ))}
        </div>
      )}
    </div>
  );
}